"use client";

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Toaster } from "react-hot-toast";

export type Language = "ar" | "en";

type LabelSet = typeof translations.en;

import { translations } from "@/i18n/translations";

interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: LabelSet;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
}

export default function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState<Language>("ar");

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem("nabddash-language");
      if (stored === "ar" || stored === "en") {
        setLanguage(stored);
      }
    } catch {
      // ignore storage errors
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = language === "ar" ? "ar" : "en";
    document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
    document.documentElement.classList.toggle("rtl", language === "ar");
    document.documentElement.classList.toggle("ltr", language === "en");
    try {
      window.localStorage.setItem("nabddash-language", language);
    } catch {
      // ignore
    }
  }, [language]);

  const value = useMemo(
    () => ({ language, setLanguage, t: translations[language] }),
    [language]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#111827",
            color: "#f1f5f9",
            border: "1px solid #1e2d45",
            fontFamily: "'Cairo', sans-serif",
            fontSize: "14px",
            direction: language === "ar" ? "rtl" : "ltr",
          },
          success: {
            iconTheme: {
              primary: "#7c3aed",
              secondary: "#111827",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "#111827",
            },
          },
        }}
      />
    </LanguageContext.Provider>
  );
}
